import {
  CUSTOM_DISCLAIMER_DELIVERY_COOLDOWN_SECONDS,
} from '../constants/customDisclaimers';
import type {
  CustomDisclaimer,
  CustomDisclaimerApiRecord,
  CustomDisclaimerApiWriteRequest,
  DisclaimerDraft,
  DisclaimerPolicy,
  DisclaimerPolicyOption,
} from '../types/customDisclaimers';
import { normalizeDisclaimerDraft } from './customDisclaimerUtils';

const isAdmManagedPolicy = (policy: Pick<DisclaimerPolicy, 'name'>): boolean =>
  policy.name.endsWith('_OD');

const normalizeDisclaimerReference = (value: string): string => value.trim().toLowerCase();

/**
 * Matches a single policy reference (an id or a name) against a disclaimer's
 * `policyIds` and `policies[].id`/`policies[].name`, normalizing case and
 * surrounding whitespace on both sides of the comparison.
 */
export const findAssignedDisclaimer = (
  disclaimers: ReadonlyArray<CustomDisclaimer>,
  policyReference: string,
): CustomDisclaimer | undefined => {
  const normalizedReference = normalizeDisclaimerReference(policyReference);
  if (!normalizedReference) {
    return undefined;
  }

  return disclaimers.find((disclaimer) => {
    const matchesPolicyId = disclaimer.policyIds.some(
      (policyId) => normalizeDisclaimerReference(policyId) === normalizedReference,
    );
    const matchesPolicy = disclaimer.policies.some(
      (policy) =>
        normalizeDisclaimerReference(policy.id) === normalizedReference ||
        normalizeDisclaimerReference(policy.name) === normalizedReference,
    );

    return matchesPolicyId || matchesPolicy;
  });
};

export const toCustomDisclaimerApiWriteRequest = (
  draft: DisclaimerDraft,
): CustomDisclaimerApiWriteRequest => {
  const normalizedDraft = normalizeDisclaimerDraft(draft);

  return {
    name: normalizedDraft.name,
    organizationName: normalizedDraft.organisationName,
    message: normalizedDraft.text,
    policyIds: normalizedDraft.policyIds,
    deliveryCooldownSeconds: CUSTOM_DISCLAIMER_DELIVERY_COOLDOWN_SECONDS,
  };
};

export const toCustomDisclaimer = (
  record: CustomDisclaimerApiRecord,
): CustomDisclaimer => ({
  id: record.id,
  name: record.name,
  organisationName: record.organizationName,
  text: record.message,
  enabled: record.enabled,
  deliveryCooldownSeconds: record.deliveryCooldownSeconds,
  policyIds: [...record.policyIds],
  policies: record.policies.map((policy) => ({ id: policy.id, name: policy.name })),
  createdAt: record.createdAt,
  updatedAt: record.updatedAt,
});

export const isCustomDisclaimerApiRecord = (
  value: unknown,
): value is CustomDisclaimerApiRecord => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Partial<CustomDisclaimerApiRecord>;
  const hasValidPolicies = Array.isArray(record.policies)
    && record.policies.every((policy) =>
      Boolean(policy)
        && typeof policy.id === 'string'
        && typeof policy.name === 'string',
    );

  return typeof record.id === 'string'
    && typeof record.name === 'string'
    && typeof record.organizationName === 'string'
    && typeof record.message === 'string'
    && typeof record.enabled === 'boolean'
    && typeof record.deliveryCooldownSeconds === 'number'
    && Array.isArray(record.policyIds)
    && record.policyIds.every((policyId) => typeof policyId === 'string')
    && hasValidPolicies
    && typeof record.createdAt === 'string'
    && typeof record.updatedAt === 'string';
};

export const collectDisclaimerPolicyCandidates = (
  policies: ReadonlyArray<Pick<DisclaimerPolicy, 'id' | 'name'>>,
  disclaimers: ReadonlyArray<CustomDisclaimer>,
): DisclaimerPolicy[] => {
  const policiesById = new Map<string, DisclaimerPolicy>();

  policies.filter(isAdmManagedPolicy).forEach((policy) => {
    policiesById.set(policy.id, { id: policy.id, name: policy.name });
  });
  disclaimers.forEach((disclaimer) => {
    disclaimer.policies.filter(isAdmManagedPolicy).forEach((policy) => {
      if (!policiesById.has(policy.id)) {
        policiesById.set(policy.id, { id: policy.id, name: policy.name });
      }
    });
  });

  return Array.from(policiesById.values()).sort((left, right) => left.name.localeCompare(right.name));
};

export const toDisclaimerPolicyOptions = (
  policies: ReadonlyArray<DisclaimerPolicy>,
  disclaimers: ReadonlyArray<CustomDisclaimer>,
  currentDisclaimerId?: string,
): DisclaimerPolicyOption[] => {
  const currentDisclaimer = currentDisclaimerId
    ? disclaimers.find((disclaimer) => disclaimer.id === currentDisclaimerId)
    : undefined;

  return policies.map((policy) => {
    const owner = disclaimers.find((disclaimer) => disclaimer.policyIds.includes(policy.id));
    const assignedToCurrentDisclaimer = currentDisclaimer?.policyIds.includes(policy.id) ?? false;
    let assignmentState: DisclaimerPolicyOption['assignmentState'] = 'available';
    if (assignedToCurrentDisclaimer) {
      assignmentState = 'assigned-to-current-disclaimer';
    } else if (owner) {
      assignmentState = 'assigned-to-another-disclaimer';
    }
    const disabled = Boolean(owner && owner.id !== currentDisclaimerId);

    return {
      ...policy,
      assignmentState,
      disabled,
      disabledReason: disabled ? `Already assigned to ${owner?.name}` : undefined,
    };
  });
};